import { useApi, useCaller } from '@/hooks/api.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import {
  setGlobalActionsAction,
  setLastUpdatedAction,
  setMenusSettingAction,
  setMenusUserAction,
  setUserContextAction,
} from '@/redux/slicers/permission.slice';
import { checkActionPermission } from '@/utils/permission';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

const useActionPermission = (actions: GlobalAction[] | undefined, actionId: string) =>
  useMemo(() => checkActionPermission(actions, actionId), [actions, actionId]);

export const usePermission = () => {
  const api = useApi('');
  const caller = useCaller();
  const dispatch = useDispatch();
  const customerId = useRootSelector((state) => state.auth.customerId);
  const globalAction = useRootSelector((state) => state.permission.globalActions);

  const permissions = {
    canCreateCard: useActionPermission(globalAction, 'create_card'),
    canCreateVirtualAccount: useActionPermission(globalAction, 'create_virtual_account'),
    canCreateGroupCard: useActionPermission(globalAction, 'create_group_card'),
    canTransfer: useActionPermission(globalAction, 'transfer'),
  };

  const getAllMenuPermission = useCallback(async () => {
    const response = await caller(() => api.get(`/v1/api/customer/${customerId}/ui/menu`), {
      loadingKey: 'permission-listMenus',
    });

    if (response?.isSuccess && response.data) {
      const data = response.data;

      dispatch(setMenusUserAction(data));
      dispatch(setUserContextAction(data));
      dispatch(setGlobalActionsAction(data));
      dispatch(setLastUpdatedAction(data));
    } else {
      return;
    }
  }, [api, customerId, caller, dispatch]);

  const getAllMenuSetting = useCallback(async () => {
    const { data, isSuccess } = await caller(
      () => api.get(`/v1/api/customer/${customerId}/ui/settings-menu`),
      {
        loadingKey: 'menu-setting',
      },
    );

    if (isSuccess && data) {
      dispatch(setMenusSettingAction(data));
      dispatch(setUserContextAction(data));
    } else {
      return;
    }
  }, [api, customerId, caller, dispatch]);

  return {
    ...permissions,
    getAllMenuPermission,
    getAllMenuSetting,
  };
};
